import React, { useEffect, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { notification } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";

import {
  subscribe,
  getSnapshot,
  ensureLoaded,
  clearNotice,
  hasPendingRun,
} from "./services/deepAnalysisStore.js";
import { fmtL } from "./moneyData.js";
import { flashTitle, nativeNotify, stopTitleFlash } from "./utils/tabAlert.js";

/**
 * Tells the user their deep analysis landed, wherever they happen to be.
 *
 * Mounted inside CustomSideBar, so it is alive on every signed-in page — the
 * run takes 8–10 minutes and nobody sits on the Money page for that. Renders
 * nothing; it only watches the store and raises the banner.
 */

const NOTICE_KEY = "money-deep-analysis";

/**
 * Which notice we've already shown. Module-scoped, not a ref: every page
 * navigation remounts CustomSideBar, and a ref would re-fire the banner.
 */
let lastShownAt = 0;

function DeepAnalysisNotifier() {
  const navigate = useNavigate();
  const { notice } = useSyncExternalStore(subscribe, getSnapshot);

  // Only wake the store when a run is actually out there. Otherwise every
  // page in the app would pay for two Deep Analysis calls it never uses.
  useEffect(() => {
    if (hasPendingRun()) ensureLoaded();
  }, []);

  useEffect(() => {
    if (!notice || notice.at === lastShownAt) return;
    lastShownAt = notice.at;

    const ready = notice.kind === "ready";
    const savings = ready && notice.totalSavings > 0 ? notice.totalSavings : 0;

    const dismiss = () => {
      stopTitleFlash();
      clearNotice();
    };
    const openBrief = () => {
      dismiss();
      notification.destroy(NOTICE_KEY);
      navigate("/money");
    };

    notification.open({
      key: NOTICE_KEY,
      message: ready
        ? "Your deep analysis is ready"
        : "Your deep analysis didn't finish",
      description: (
        <span className="inline-flex items-center gap-1.5">
          {ready
            ? savings
              ? `₹${fmtL(savings)} identified — open The Brief`
              : "Open The Brief to see what needs you"
            : "Open The Brief and start a new run"}
          <ArrowRightOutlined className="text-[#027FFF]" />
        </span>
      ),
      icon: ready ? (
        <CheckCircleFilled className="text-[#059669]" />
      ) : (
        <CloseCircleFilled className="text-[#DC2626]" />
      ),
      placement: "topRight",
      // Sticky: the user may be in another browser tab when this fires.
      duration: 0,
      onClick: openBrief,
      onClose: dismiss,
      className: "cursor-pointer",
    });

    flashTitle(ready ? "✅ Analysis ready" : "⚠️ Analysis failed");
    nativeNotify({
      title: ready
        ? "Deep analysis ready · Forcesight"
        : "Deep analysis failed · Forcesight",
      body: ready
        ? "Your money findings are in. Click to open The Brief."
        : "The run didn't finish. Click to open The Brief.",
      onClick: openBrief,
    });
  }, [notice, navigate]);

  return null;
}

export default DeepAnalysisNotifier;
